import React, { useEffect, useMemo, useState, useContext } from "react";
import {
	Breadcrumb,
	Button,
	Flex,
	Spin,
	notification,
	Select,
	Typography,
	Card,
	Empty,
} from "antd";
import axios from "axios";
import Areas from "../components/Areas";
import filterOption from "../../../components/filterOption";
import { UserContext } from "../components/privateroute";

const makeNewArea = (name = "Obszar 1") => ({
	id: `new${Date.now()}`,
	name,
	woj: null,
	powiat: null,
	gmina: null,
});

const normalizeArea = (area) => ({
	id: area?.id ?? area?.swuseractionareaid ?? `new${Date.now()}`,
	name: area?.name ?? "Obszar",
	woj: area?.woj ?? null,
	powiat: area?.powiat ?? null,
	gmina: area?.gmina ?? null,
});

export default function ObszaryDzialania() {
	const globalUser = useContext(UserContext);
	const isAdmin = Boolean(globalUser?.isadmin);
	const [areas, setAreas] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [usersLoading, setUsersLoading] = useState(false);
	const [users, setUsers] = useState([]);
	const [selectedUserId, setSelectedUserId] = useState(null);

	useEffect(() => {
		if (!isAdmin) return;
		let mounted = true;
		const fetchUsers = async () => {
			setUsersLoading(true);
			try {
				const response = await axios.post("/sprawy/get/users");
				const list = Array.isArray(response?.data) ? response.data : [];
				const active = list.filter(
					(x) => Number(x?.mlg_users_active) === 1,
				);
				const options = active
					.map((x) => ({
						label: x?.mlg_users_fullname,
						value: x?.mlg_users_id,
					}))
					.filter((x) => x.value != null);

				if (!mounted) return;
				setUsers(options);
				if (options.length > 0) {
					setSelectedUserId((prev) =>
						prev != null ? prev : options[0].value,
					);
				}
			} catch (error) {
				if (!mounted) return;
				notification.error({
					message: "Nie udało się pobrać listy pracowników",
					duration: 3,
				});
				setUsers([]);
				setSelectedUserId(null);
			} finally {
				if (mounted) setUsersLoading(false);
			}
		};
		fetchUsers();
		return () => {
			mounted = false;
		};
	}, [isAdmin]);

	useEffect(() => {
		let mounted = true;
		const fetchAreasForSelectedUser = async () => {
			if (isAdmin && !selectedUserId) return;
			if (!isAdmin && !globalUser?.user?.mlg_users_id) return;
			setLoading(true);
			try {
				const response = isAdmin
					? await axios.post("/admin/user/areas/get", {
							userid: selectedUserId,
						})
					: await axios.post("/user/areas/get");
				const list =
					response?.data?.data && Array.isArray(response.data.data)
						? response.data.data
						: [];
				if (!mounted) return;
				setAreas(list.map(normalizeArea));
			} catch (error) {
				if (!mounted) return;
				notification.error({
					message: "Nie udało się pobrać obszarów działań",
					duration: 3,
				});
				setAreas([]);
			} finally {
				if (mounted) setLoading(false);
			}
		};
		fetchAreasForSelectedUser();
		return () => {
			mounted = false;
		};
	}, [selectedUserId, isAdmin, globalUser]);

	const payload = useMemo(() => {
		return areas.map((a) => ({
			name: (a?.name ?? "Obszar").toString().trim() || "Obszar",
			woj: a?.woj ?? null,
			powiat: a?.powiat ?? null,
			gmina: a?.gmina ?? null,
		}));
	}, [areas]);

	const save = async () => {
		if (isAdmin && !selectedUserId) {
			notification.error({
				message: "Wybierz pracownika",
				duration: 3,
			});
			return;
		}
		// Minimal requirement to make territory filtering meaningful.
		if (payload.some((a) => a.woj == null)) {
			notification.error({
				message: "Wybierz województwo w każdym obszarze",
				duration: 3,
			});
			return;
		}

		setSaving(true);
		try {
			if (isAdmin) {
				await axios.post("/admin/user/areas/save", {
					userid: selectedUserId,
					areas: payload,
				});
			} else {
				await axios.post("/user/areas/save", {
					areas: payload,
				});
			}
			notification.success({
				message: "Zapisano obszary działań",
				duration: 2,
			});
		} catch (error) {
			notification.error({
				message: "Nie udało się zapisać obszarów działań",
				duration: 3,
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Breadcrumb style={{ padding: "20px" }}>
				<Breadcrumb.Item>Strona główna</Breadcrumb.Item>
				<Breadcrumb.Item>Obszary działań</Breadcrumb.Item>
			</Breadcrumb>
			<div style={{ padding: "20px", width: "100%" }}>
				<Flex
					gap={16}
					style={{ width: "100%" }}
					align="stretch"
				>
					{isAdmin ? (
						<Card
							title="Pracownik"
							style={{ width: 360 }}
							bodyStyle={{
								display: "flex",
								flexDirection: "column",
								gap: 12,
							}}
						>
							<Typography.Text type="secondary">
								Wybierz pracownika, któremu chcesz przydzielić
								obszary.
							</Typography.Text>
							<Select
								showSearch
								filterOption={filterOption}
								loading={usersLoading}
								options={users}
								value={selectedUserId}
								onChange={(value) => setSelectedUserId(value)}
								placeholder="Wybierz pracownika"
								style={{ width: "100%" }}
								allowClear
							/>
						</Card>
					) : (
						<Card
							title="Twoje obszary"
							style={{ width: 360 }}
							bodyStyle={{
								display: "flex",
								flexDirection: "column",
								gap: 12,
							}}
						>
							<Typography.Text type="secondary">
								Zmieniaj swoje obszary działań, aby widzieć
								sprawy z innych terenów.
							</Typography.Text>
						</Card>
					)}

					<Card
						title="Obszary działań"
						style={{ flex: 1 }}
						extra={
							<Button
								type="primary"
								loading={saving}
								onClick={save}
								disabled={isAdmin && !selectedUserId}
							>
								Zapisz
							</Button>
						}
					>
						<Spin spinning={loading}>
							{isAdmin ? (
								selectedUserId ? (
									<Flex
										vertical
										gap={16}
									>
										<Areas
											areas={areas}
											setAreas={setAreas}
										/>
									</Flex>
								) : (
									<Empty description="Wybierz pracownika po lewej stronie" />
								)
							) : (
								<Flex
									vertical
									gap={16}
								>
									<Areas
										areas={areas}
										setAreas={setAreas}
									/>
								</Flex>
							)}
						</Spin>
					</Card>
				</Flex>
			</div>
		</>
	);
}
